/**
 * Rasterize a visualization shell to a PNG file.
 * HTMLElement refs capture the enclosing MUI Paper when present (page cards
 * keep padding; margin and shadow are stripped). Recharts `{ container }` refs
 * keep stacked-area export padding.
 */
import { useRef } from "react"

import saveAs from "file-saver"
import html2canvas from "html2canvas"

import { LEFT_AREA_CHART_ADDITIONAL_SPACING } from "../stacked-area-graph/stacked-area-graph.types"
import type {
  GraphPngCurrent,
  GraphToPng,
  RechartsPngContainer,
} from "./graph-to-png.types"

const RECHARTS_EXPORT_PADDING_PX = 48
const MUI_PAPER_ROOT_SELECTOR = ".MuiPaper-root"

/** Mark overflow chrome with this attribute so PNG capture can hide it. */
export const GRAPH_PNG_EXCLUDE_ATTR = "data-graph-png-exclude"
const GRAPH_PNG_EXCLUDE_SELECTOR = `[${GRAPH_PNG_EXCLUDE_ATTR}]`

interface PngCaptureTarget {
  extraPadding: boolean
  source: HTMLElement
}

/** Minimum 2× so exports look sharp on 1× displays; cap at 3× for file size. */
const getPngExportScale = (): number => {
  if (globalThis.window === undefined) {
    return 2
  }
  return Math.min(3, Math.max(2, globalThis.window.devicePixelRatio))
}

const isRechartsContainerRef = (
  value: unknown
): value is RechartsPngContainer =>
  typeof value === "object" &&
  value !== null &&
  "container" in value &&
  value.container instanceof HTMLElement

const getEnclosingPaperOrSelf = (node: HTMLElement): HTMLElement => {
  const paper = node.closest(MUI_PAPER_ROOT_SELECTOR)
  return paper instanceof HTMLElement ? paper : node
}

const resolvePngCaptureTarget = (
  current: GraphPngCurrent | null
): PngCaptureTarget => {
  if (current instanceof HTMLElement) {
    return { extraPadding: false, source: getEnclosingPaperOrSelf(current) }
  }
  if (isRechartsContainerRef(current)) {
    return { extraPadding: true, source: current.container }
  }
  throw new Error("PNG capture target is missing")
}

const hideClonedElements = (
  clonedElement: HTMLElement,
  selectors: string[]
): void => {
  selectors.forEach((selector) => {
    clonedElement.querySelectorAll(selector).forEach((element) => {
      if (element instanceof HTMLElement || element instanceof SVGElement) {
        element.style.display = "none"
      }
    })
  })
}

const flattenClonedCardChrome = (clonedElement: HTMLElement): void => {
  clonedElement.style.boxShadow = "none"
  clonedElement.style.margin = "0"
}

const buildExportContainer = (
  source: HTMLElement,
  extraPadding: boolean
): { clonedElement: HTMLElement; exportContainer: HTMLElement } => {
  const exportContainer = document.createElement("div")
  const clonedElement = source.cloneNode(true)
  if (!(clonedElement instanceof HTMLElement)) {
    throw new TypeError("PNG capture target is missing")
  }

  const padding = extraPadding ? RECHARTS_EXPORT_PADDING_PX : 0
  const leftPadding = extraPadding
    ? padding - LEFT_AREA_CHART_ADDITIONAL_SPACING
    : 0

  Object.assign(exportContainer.style, {
    height: `${source.offsetHeight + padding * 2}px`,
    left: "-9999px",
    padding: extraPadding
      ? `${padding}px ${padding}px ${padding}px ${leftPadding}px`
      : "0",
    position: "absolute",
    top: "0",
    width: `${source.offsetWidth + padding * 2}px`,
  })
  if (extraPadding) {
    clonedElement.style.position = "relative"
  }
  exportContainer.appendChild(clonedElement)
  if (!extraPadding) {
    flattenClonedCardChrome(clonedElement)
  }

  return { clonedElement, exportContainer }
}

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob)
    }, "image/png")
  })

export function useGraphToPng(): GraphToPng {
  const ref = useRef<GraphPngCurrent | null>(null)
  const savePng = async (fileName: string, exportHiddenElements?: string[]) => {
    const { extraPadding, source } = resolvePngCaptureTarget(ref.current)
    const { clonedElement, exportContainer } = buildExportContainer(
      source,
      extraPadding
    )
    hideClonedElements(clonedElement, [
      GRAPH_PNG_EXCLUDE_SELECTOR,
      ...(exportHiddenElements ?? []),
    ])
    document.body.appendChild(exportContainer)

    try {
      const canvas = await html2canvas(exportContainer, {
        logging: false,
        scale: getPngExportScale(),
      })
      const blob = await canvasToBlob(canvas)
      if (blob) {
        saveAs(blob, `${fileName}.png`)
      }
    } finally {
      exportContainer.remove()
    }
  }

  return {
    ref,
    savePng,
  }
}
